import { useEffect } from 'react';
import { useToggleHide } from './modules/project/useToggleHide';
import { ProjectShellFragment } from '../types/graphqlTypes';
import { CameraMode } from '../potree/defines';
import { RendererContextType } from '../contexts/RendererContext';

/**
 * Side-effects hook for ViewManager integration.
 *
 * Handles syncing map and WMS layer visibility to Apollo/DB when switching to 3D.
 *
 * Must receive viewer directly (not via RendererContext) because it's mounted
 * in the same component that provides RendererContext.
 */
export function useViewManagerSideEffects(
  viewer: RendererContextType['viewer'],
  project?: ProjectShellFragment,
): void {
  const { toggleHide } = useToggleHide();

  // Sync map and WMS layer visibility to Apollo/DB when switching to 3D
  useEffect(() => {
    const viewManager = viewer?.personaManager?.viewManager;
    if (!viewManager) return;

    const handleViewChanged = (event: { view?: { cameraMode?: number } }) => {
      const view = event.view;
      if (view?.cameraMode !== CameraMode.ORTHOGRAPHIC) {
        const identifiersToHide: string[] = [];
        if (project?.mapVisible) {
          identifiersToHide.push('map');
        }
        const visibleWmsLayers = project?.state?.wmsLayers?.filter((layer) => layer.visible) ?? [];
        visibleWmsLayers.forEach((layer) => identifiersToHide.push(layer.identifier));
        if (identifiersToHide.length > 0) {
          toggleHide({ identifiers: identifiersToHide, visible: false });
        }
      }
    };

    viewManager.addEventListener('view_changed', handleViewChanged);
    return () => {
      viewManager.removeEventListener('view_changed', handleViewChanged);
    };
  }, [viewer, toggleHide, project?.mapVisible, project?.state?.wmsLayers]);
}
